import { useCallback, useEffect, useRef, useState } from 'react';

import { apiDelete, apiGet, apiPost, responseErrorMessage } from '../lib/api_v2';
import db, { clearFingerprint } from '../lib/db';
import { logError } from '../lib/logger';

// A locally stood-up board item, replaced the moment the server's own row for the same
// widget arrives. The prefix is what marks it as ours: the server never mints ids in this
// shape, so the board can tell the two apart without extra bookkeeping.
const OPTIMISTIC_BOARD_ITEM_ID_PREFIX = 'optimistic:';
const OPTIMISTIC_BOARD_ITEM_SORT_GAP = 1000;

function optimisticBoardItemId(widgetId) {
  return `${OPTIMISTIC_BOARD_ITEM_ID_PREFIX}${widgetId}`;
}

export function isOptimisticBoardItemId(boardItemId) {
  return String(boardItemId || '').startsWith(OPTIMISTIC_BOARD_ITEM_ID_PREFIX);
}

function isAlreadyInstalledMessage(message) {
  return message === 'widget template is already installed in this workspace';
}

export function isSingleInstallTemplateInstalled(template) {
  return Boolean(template?.install_once_per_workspace && template?.installed);
}

function updateTemplate(setTemplates, slug, updates) {
  setTemplates((currentTemplates) => currentTemplates.map((template) => (template.slug === slug ? { ...template, ...updates } : template)));
}

async function fetchWorkspaceTemplate(workspaceId, slug) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  const response = await apiGet(`/widget_templates?${query.toString()}`);
  const data = await response.json();

  if (!response.ok || !data.ok || !Array.isArray(data.data?.widget_templates)) {
    throw new Error(responseErrorMessage(data, 'Could not refresh widget template state.'));
  }

  return data.data.widget_templates.find((template) => template.slug === slug) || null;
}

export function useWidgetTemplateActions({ canUseWidgetTemplates, workspaceId, channelId, setTemplates, source, onError }) {
  const [installingSlug, setInstallingSlug] = useState(null);
  const [removingSlug, setRemovingSlug] = useState(null);
  const [installedSlug, setInstalledSlug] = useState(null);
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;
  const isBusy = Boolean(installingSlug || removingSlug);

  useEffect(
    function resetActionStateForWorkspace() {
      setInstallingSlug(null);
      setRemovingSlug(null);
      setInstalledSlug(null);
    },
    [workspaceId],
  );

  const reportError = useCallback((message, template) => onError?.(message, template), [onError]);
  const isCurrentWorkspaceAction = useCallback((actionWorkspaceId) => workspaceIdRef.current === actionWorkspaceId, []);

  const installTemplate = useCallback(
    async function installTemplate(template) {
      if (!canUseWidgetTemplates || !workspaceId || !channelId || isBusy || !template) return;
      if (isSingleInstallTemplateInstalled(template)) return;

      const actionWorkspaceId = workspaceId;
      const actionChannelId = channelId;
      const pendingBoardItemId = optimisticBoardItemId(template.slug);
      setInstallingSlug(template.slug);
      setInstalledSlug(null);
      reportError(null, template);

      try {
        // Before the request, not after it: the board renders from board_items, and waiting
        // on the round trip is exactly the gap between hitting Add and seeing anything. The
        // row starts without a widget — the board draws a placeholder for it — and picks one
        // up when the response lands.
        await db.transaction('rw', db.board_items, async () => {
          const existing = await db.board_items.where('[workspace_id+boardable_type]').equals([actionWorkspaceId, 'widget']).toArray();
          // Ahead of everything, because that is where the installed widget lands: the board
          // sorts on sort_order and breaks ties by newest updated_at, so a new widget reads
          // at the top. Putting the placeholder anywhere else makes the tile jump when the
          // real row replaces it. updated_at is set for the tie case.
          const lowestSortOrder = existing.reduce((lowest, item) => Math.min(lowest, item.sort_order || 0), 0);

          await db.board_items.put({
            workspace_id: actionWorkspaceId,
            id: pendingBoardItemId,
            boardable_type: 'widget',
            boardable_id: null,
            pinned: false,
            sort_order: lowestSortOrder - OPTIMISTIC_BOARD_ITEM_SORT_GAP,
            updated_at: Date.now(),
            pending_title: template.listing?.display_name || template.name || 'Widget',
          });
        });

        const response = await apiPost(`/widget_templates/${encodeURIComponent(template.slug)}/install`, {
          widget_template: {
            workspace_id: actionWorkspaceId,
            channel_id: actionChannelId,
          },
        });
        const data = await response.json();

        if (!response.ok || !data.ok) {
          const message = responseErrorMessage(data, 'Could not install widget.');
          if (template.install_once_per_workspace && isAlreadyInstalledMessage(message)) {
            const refreshedTemplate = await fetchWorkspaceTemplate(actionWorkspaceId, template.slug);
            if (!isCurrentWorkspaceAction(actionWorkspaceId)) return;
            if (refreshedTemplate) {
              updateTemplate(setTemplates, template.slug, refreshedTemplate);
              reportError(null, template);
              return;
            }
          }

          throw new Error(message);
        }
        if (!isCurrentWorkspaceAction(actionWorkspaceId)) return;

        const widget = data.data?.widget;
        if (widget) {
          await db.widgets.put(widget);
          // The placeholder becomes the real tile in place, so nothing is inserted twice
          // and the widget never jumps position between pending and installed.
          await db.board_items.update([actionWorkspaceId, pendingBoardItemId], { boardable_id: widget.id, pending_title: null });
          await clearFingerprint('widgets');
          await clearFingerprint('board_items');
        }

        setInstalledSlug(template.slug);
        if (template.install_once_per_workspace) {
          updateTemplate(setTemplates, template.slug, {
            installed: true,
            installed_widget_id: widget?.id || template.installed_widget_id,
          });
        }
      } catch (error) {
        logError(source, 'Failed to install widget template', { error, templateSlug: template.slug });
        if (isCurrentWorkspaceAction(actionWorkspaceId)) {
          reportError(error.message || 'Could not install widget.', template);
        }
      } finally {
        // Covers the early returns too (already-installed, workspace switched): a
        // placeholder that never resolved must not outlive the request that made it.
        try {
          const settled = await db.board_items.get([actionWorkspaceId, pendingBoardItemId]);
          if (settled && !settled.boardable_id) await db.board_items.delete([actionWorkspaceId, pendingBoardItemId]);
        } catch (cleanupError) {
          logError(source, 'Failed to clean up optimistic widget install', { error: cleanupError, templateSlug: template.slug });
        }
        if (isCurrentWorkspaceAction(actionWorkspaceId)) {
          setInstallingSlug(null);
        }
      }
    },
    [canUseWidgetTemplates, channelId, isBusy, isCurrentWorkspaceAction, reportError, setTemplates, source, workspaceId],
  );

  const removeTemplate = useCallback(
    async function removeTemplate(template) {
      if (!canUseWidgetTemplates || !workspaceId || isBusy || !template?.installed_widget_id) return;

      const actionWorkspaceId = workspaceId;
      setRemovingSlug(template.slug);
      reportError(null, template);

      try {
        const response = await apiDelete(`/widgets/${encodeURIComponent(template.installed_widget_id)}`);
        const data = await response.json();

        if (!response.ok || !data.ok) {
          throw new Error(responseErrorMessage(data, 'Could not remove widget.'));
        }
        if (!isCurrentWorkspaceAction(actionWorkspaceId)) return;

        await db.transaction('rw', db.widgets, db.widget_runtime_titles, async () => {
          await db.widgets.delete([actionWorkspaceId, template.installed_widget_id]);
          await db.widget_runtime_titles.delete([actionWorkspaceId, template.installed_widget_id]);
        });
        await clearFingerprint('widgets');
        await clearFingerprint('board_items');
        let refreshedTemplate = null;
        try {
          refreshedTemplate = await fetchWorkspaceTemplate(actionWorkspaceId, template.slug);
        } catch (refreshError) {
          logError(source, 'Failed to refresh widget template state after remove', { error: refreshError, templateSlug: template.slug });
        }
        if (!isCurrentWorkspaceAction(actionWorkspaceId)) return;

        setInstalledSlug(null);
        updateTemplate(setTemplates, template.slug, refreshedTemplate || { installed: false, installed_widget_id: null });
      } catch (error) {
        logError(source, 'Failed to remove widget template', { error, templateSlug: template.slug });
        if (isCurrentWorkspaceAction(actionWorkspaceId)) {
          reportError(error.message || 'Could not remove widget.', template);
        }
      } finally {
        if (isCurrentWorkspaceAction(actionWorkspaceId)) {
          setRemovingSlug(null);
        }
      }
    },
    [canUseWidgetTemplates, isBusy, isCurrentWorkspaceAction, reportError, setTemplates, source, workspaceId],
  );

  const canInstallTemplate = useCallback(
    (template, { isLoading = false } = {}) =>
      Boolean(
        canUseWidgetTemplates &&
          workspaceId &&
          channelId &&
          template &&
          !isSingleInstallTemplateInstalled(template) &&
          !isBusy &&
          !isLoading,
      ),
    [canUseWidgetTemplates, channelId, isBusy, workspaceId],
  );

  const canRemoveTemplate = useCallback(
    (template, { isLoading = false } = {}) =>
      Boolean(canUseWidgetTemplates && workspaceId && template?.installed_widget_id && !isBusy && !isLoading),
    [canUseWidgetTemplates, isBusy, workspaceId],
  );

  const resetInstalledSlug = useCallback(() => setInstalledSlug(null), []);

  return {
    installingSlug,
    removingSlug,
    installedSlug,
    installTemplate,
    removeTemplate,
    canInstallTemplate,
    canRemoveTemplate,
    resetInstalledSlug,
  };
}
